import { TFunction } from 'next-i18next';
import { AxiosResponse } from 'axios';
import { NextApiRequestQuery } from 'next/dist/server/api-utils';
import { toDateWithTime } from '../datetime';
import {
    CompetitionYearInterface,
    Option,
} from 'components/interfaces/GeneralInterface';
import { getClubList, getClubListAdminOnly } from 'services/club.service';
import {
    ClubListItemInterface,
    ClubListItemRootInterface,
} from 'components/interfaces/ClubInterface';
import {
    getCopmetitionDatesList,
    getCompetitionList,
    getCompetitionTeamList,
} from 'services/competition.service';
import {
    CompetitionApiInterface,
    CompetitionDateInterface,
    CompetitionDatesInterface,
    CompetitionTeamApiInterface,
    CompetitionTeamStateApiEnum,
    MemberPlayerListApiInterface,
    PitchApiInterface,
    UserOrganisationUnitApiInterface,
    TransactionsTemplatesListApiInterface,
    MatchApiInterface,
    OrganizationUnitRolesApiInterface,
    OrganisationUnitRoleApiEnum,
    LegalPersonApiInterface,
    AccountDocumentApiInterface,
    ClubReferenceApiInterface,
    SubsidyListApiInterface,
    MemberListApiInterface,
    AgentApiInterface,
    PaginatorInterface,
    LicenseRoleEnum,
    AccountSubsidyListApiInterface,
} from 'submodules/api_middleware';
import { PitchService } from 'services/pitch.service';
import { getMemberList } from 'services/member.service';
import { getSubisdyList, getSubisdyListFull } from 'services/subsidy.service';
import {
    getDocuments,
    getSubsidiesDocuments,
    getTransactionTemplates,
} from 'services/account.service';
import { MatchService } from 'services/match.service';
import { getLegalPersonList } from 'services/legalPerson.service';
import { PersonService } from 'services/person.service';
import { getAgents } from 'services/agent.service';
import { UserService } from 'services/user.service';
import { UserOrganisationUnitRole } from 'components/interfaces/MembershipPaymentInterface';
import { getCompetitionYears } from 'services/list.service';
import { getPlayerList } from 'services/player.service';
import { LicenseService } from 'services/license.service';
import { CoachService } from 'services/coach';
import { TNamespaceEnum } from 'types/types';
import { getTranslation } from 'utils/localization';

export const findClubsByInternalId = async (
    value: NextApiRequestQuery
): Promise<Option[]> => {
    const result = await getClubList(value);
    return transformClubsToOptions(result?.data, 'internal_id');
};

export const findClubsById = async (
    value: NextApiRequestQuery
): Promise<Option[]> => {
    const result = await getClubList(value);
    return transformClubsToOptions(result?.data, 'id');
};

export const enum ClubsAccessLevel {
    All = 'All',
    AdminOnly = 'AdminOnly',
}

export const findClubs = async (
    value: NextApiRequestQuery,
    accessLevel: ClubsAccessLevel
): Promise<Option[]> => {
    const result =
        accessLevel === ClubsAccessLevel.AdminOnly
            ? await getClubListAdminOnly(value)
            : await getClubList(value);
    return (
        result?.data?.map(
            (obj: ClubListItemRootInterface): Option => ({
                label: `${obj.details.name} (${obj.details.internal_id})`,
                value: obj.id,
            })
        ) || []
    );
};

export const transformClubsToOptions = (
    clubs: ClubListItemInterface[],
    property: 'id' | 'internal_id'
): Option[] =>
    clubs?.map(
        (club: ClubListItemInterface): Option => ({
            label: `${club.name} (${club.internal_id})`,
            value: club[`${property}` as 'id' | 'internal_id'],
        })
    ) || [];

export const findCompetitions = async (
    query: NextApiRequestQuery
): Promise<Option[]> => {
    const response = await getCompetitionList(query);
    const competitions =
        response?.data?.map((competition: CompetitionApiInterface) => ({
            label: `${competition.name} (${competition.identifier})`,
            value: competition.id,
        })) || [];
    return competitions;
};

export const findCompetitionDates = async (
    t: TFunction,
    competition_id: number,
    setDates?: (dates: CompetitionDateInterface[]) => void
): Promise<Option[]> => {
    const response = await getCopmetitionDatesList(competition_id);
    const dates = response?.data as CompetitionDatesInterface;
    const dateData: CompetitionDateInterface[] = [];

    const options: Option[] = [];
    dates?.autumn?.forEach((date) => {
        if (setDates) dateData.push(date);
        options.push(transformDatesOnOptions(t, date));
    });

    dates?.spring?.forEach((date) => {
        if (setDates) dateData.push(date);
        options.push(transformDatesOnOptions(t, date));
    });

    if (setDates) {
        setDates(dateData);
    }
    return options;
};

export const transformDatesOnOptions = (
    t: TFunction,
    date: CompetitionDateInterface
): Option => ({
    label: `${t(`CompetitionDatesPage.${date.season}`)} ${
        '- ' +
        date.number +
        (date.number_berger ? `(${date.number_berger})` : '')
    } ${date.datetime ? '- ' + toDateWithTime(date.datetime) : ''}`,
    value: date.number,
});

export const findPitches = async (
    query: NextApiRequestQuery
): Promise<Option[]> => {
    const response = await new PitchService().getList(query);

    const pitches: Option[] =
        response?.data?.map((pitch: PitchApiInterface) => ({
            label: pitch.name,
            value: pitch.id,
        })) || [];
    return pitches;
};

export type CompetitionTeamQueryType = NextApiRequestQuery & {
    competition_id: string;
};

export const findCompetitionApprovedTeams = async (
    query: CompetitionTeamQueryType,
    setTeams?: (teams: CompetitionTeamApiInterface[]) => void,
    setPaginator?: (paginator: PaginatorInterface) => void
): Promise<Option[]> => {
    const response = await getCompetitionTeamList({
        ...query,
        state: CompetitionTeamStateApiEnum.APPROVED,
    });

    let teams: Option[] = [];
    if (response?.data.data) {
        setTeams && setTeams(response.data.data);
        setPaginator && setPaginator(response.data.meta);
        teams = transformTeamsOnOptions(response.data.data);
    }

    return teams;
};

export const transformTeamsOnOptions = (
    teams: CompetitionTeamApiInterface[]
): Option[] =>
    teams?.map(
        (team: CompetitionTeamApiInterface): Option => ({
            label: team.name,
            value: team.id,
        })
    ) || [];

export const findCompetitionYears = async (): Promise<Option[]> => {
    const years = await getCompetitionYears();
    return transformCompetitionYearOnOptions(years);
};

export const transformCompetitionYearOnOptions = (
    competitionYears: CompetitionYearInterface[]
): Option[] =>
    competitionYears?.map((year) => ({
        label: year.season,
        value: year.year,
    })) || [];

export const findPlayers = async (
    query: NextApiRequestQuery
): Promise<Option[]> => {
    const response = await getPlayerList(query);
    return response.data.data.map((member: MemberPlayerListApiInterface) => ({
        label: `${member.person.name} (${member.member?.internal_id})`,
        value: member.person.id,
    }));
};

export const findMembers = async (
    value: NextApiRequestQuery
): Promise<Option[]> => {
    const result = await getMemberList(value);

    return (
        result?.data?.data?.map(
            (obj: MemberPlayerListApiInterface): Option => ({
                label: `${obj.person.name} (${obj.member?.internal_id})`,
                value: obj.person.id,
            })
        ) || []
    );
};

export const transformMemberOnOptions = (
    members: MemberPlayerListApiInterface[]
): Option[] =>
    members?.map(({ person, member }: MemberPlayerListApiInterface) => ({
        label: `${person.name} (${member?.internal_id})`,
        value: person.id,
    })) || [];

export const findTransactionsTemplates = async (
    query: NextApiRequestQuery
): Promise<{
    options: Option[];
    data: TransactionsTemplatesListApiInterface[];
}> => {
    const result = await getTransactionTemplates(query);

    return {
        data: result?.data || {},
        options:
            result?.data?.map(
                (obj: TransactionsTemplatesListApiInterface): Option => ({
                    label: `${obj.name}`,
                    value: obj.id,
                })
            ) || [],
    };
};

export const transformOrgUnitOnOptions = (
    orgUnits: UserOrganisationUnitApiInterface[]
): Option[] =>
    orgUnits?.map((unit) => ({
        label: `${unit.name} (${unit.code})`,
        value: unit.id,
    })) || [];

export const transformOrgUnitRolesOnOptions = (
    t: TFunction,
    orgUnitRoles: OrganizationUnitRolesApiInterface[],
    withoutOrgUnitAdmin?: boolean
): Option[] => {
    if (withoutOrgUnitAdmin) {
        orgUnitRoles = orgUnitRoles?.filter(
            (unit) =>
                unit.role !==
                OrganisationUnitRoleApiEnum.organisation_unit_admin
        );
    }
    return (
        orgUnitRoles?.map((unit) => ({
            label: t(`OrganisationRole.${unit.role}`),
            value: unit.role,
        })) || []
    );
};

export const findPureMatches = (
    query: NextApiRequestQuery
): Promise<AxiosResponse<MatchApiInterface[]>> =>
    new MatchService().getListFull(query);

export const findPureSubsidies = (
    query: NextApiRequestQuery
): Promise<AxiosResponse<SubsidyListApiInterface[]>> =>
    getSubisdyListFull(query);

export const findOrgUnitRoles = (
    orgUnitId: number,
    query: NextApiRequestQuery
): Promise<AxiosResponse<UserOrganisationUnitRole[]>> =>
    new UserService().myOrganisationUnitRoles(orgUnitId, query);

export const transformUserOrgUnitRolesOnOptions = (
    t: TFunction,
    orgUnitRoles: UserOrganisationUnitRole[]
): Option[] => {
    return (
        orgUnitRoles?.map((userOrgRole) => ({
            label: t(`OrganisationRole.${userOrgRole.role}`),
            value: userOrgRole.role,
        })) || []
    );
};

export const findMatches = async (
    query: NextApiRequestQuery
): Promise<Option[]> => {
    const response = await new MatchService().getList(query);

    return transformMatchesOnOptions(response?.data);
};

export const transformMatchesOnOptions = (
    matches: MatchApiInterface[]
): Option[] =>
    matches?.map((match) => ({
        label: match.identifier,
        value: match.id,
    })) || [];

export const findSubsidies = async (
    query: NextApiRequestQuery
): Promise<Option[]> => {
    const response = await getSubisdyList(query);

    return (
        response?.data?.map((subsidy: SubsidyListApiInterface) => ({
            label: `${subsidy.name}`,
            value: subsidy.id,
        })) || []
    );
};

export const findLegalPersons = async (
    query: NextApiRequestQuery
): Promise<Option[]> => {
    const response = await getLegalPersonList(query);

    return (
        response?.data?.map((legalPerson: LegalPersonApiInterface) => ({
            label: `${legalPerson.name} ${
                legalPerson.internal_id ? `(${legalPerson.internal_id})` : ''
            }`,
            value: legalPerson.id,
        })) || []
    );
};

export const findPersons = async (
    query: NextApiRequestQuery
): Promise<Option[]> => {
    const response = await new PersonService().getPersonList(query);

    return (
        response?.data?.map(
            (person: MemberListApiInterface): Option => ({
                label: `${person.person.name} ${
                    person.member?.internal_id
                        ? `(${person.member?.internal_id})`
                        : ''
                }`,
                value: person.person.id,
            })
        ) || []
    );
};

export const findDocuments = async (
    query: NextApiRequestQuery
): Promise<Option[]> => {
    const result = await getDocuments(query);

    return (
        result?.data?.map(
            (obj: AccountDocumentApiInterface): Option => ({
                label: `${obj.variable_symbol} (${
                    (obj.owner.reference as ClubReferenceApiInterface).name
                })`,
                value: obj.id,
            })
        ) || []
    );
};

export const findSubsidyDocuments = async (
    query: NextApiRequestQuery
): Promise<Option[]> => {
    const result = await getSubsidiesDocuments(query);

    return (
        result?.data?.map(
            (obj: AccountSubsidyListApiInterface): Option => ({
                label: `${obj.document.variable_symbol} (${
                    (obj.document.owner.reference as ClubReferenceApiInterface)
                        .name
                })`,
                value: obj.document.id,
            })
        ) || []
    );
};

export const findAgents = async (
    query: NextApiRequestQuery
): Promise<Option[]> => {
    const result = await getAgents(query);

    return (
        result?.data?.map((agent: AgentApiInterface): Option => {
            const obj = agent.legal_person ? agent.legal_person : agent.person;

            return {
                label: `${obj.name} ${
                    obj.internal_id ? `(${obj.internal_id})` : ''
                }`,
                value: agent.id,
            };
        }) || []
    );
};

export const findLicenseTypes = async (
    t: TFunction,
    role: LicenseRoleEnum
): Promise<Option[]> => {
    const licences = await new LicenseService().getRoleTypes(role);

    return (
        licences?.data?.map((licence) => ({
            value: licence,
            label: getTranslation(
                t,
                TNamespaceEnum.person,
                `license.${licence}`
            ),
        })) || []
    );
};

/** Options for coaches list filter — IS1 licence types from GET /v1/coaches/license-types. */
export const findCoachLicenceTypes = async (
    _t: TFunction
): Promise<Option[]> => {
    const licences = await new CoachService().getLicenceTypes();
    const rows = licences?.data;
    if (!Array.isArray(rows)) {
        return [];
    }

    return rows.map((licence) => ({
        value: licence.id,
        label: licence.name,
    }));
};
